import { Box, Chip, Stack, Typography } from '@mui/material';
import { forwardRef } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { TranscriptionBlockData, TranscriptionWordData } from '../lib/transcription.types';
import { TranscriptionWord } from './TranscriptionWord';

const musicSegmentMessage = defineMessage({
  id: 'audit.transcriptionScreen.music',
  defaultMessage: '[Música/Silencio]',
});

type TranscriptionBlockItemProps = {
  block: TranscriptionBlockData;
  blockActive: boolean;
  showWordEfficiency: boolean;
  matchWordIds: Set<string>;
  onSeekSegment: () => void;
  onWordClick: (wordStartSeconds: number) => void;
  isWordHighlighted: (word: TranscriptionWordData, blockActive: boolean) => boolean;
};

/**
 * One transcript row: timestamp, optional speaker line, and words (or music placeholder).
 * The row is clickable to seek to the segment start (YouTube-style block click).
 */
export const TranscriptionBlockItem = forwardRef<HTMLDivElement, TranscriptionBlockItemProps>(
  function TranscriptionBlockItem(
    {
      block,
      blockActive,
      showWordEfficiency,
      matchWordIds,
      onSeekSegment,
      onWordClick,
      isWordHighlighted,
    },
    ref,
  ) {
    const { formatMessage } = useIntl();

    return (
      <Box
        ref={ref}
        onClick={onSeekSegment}
        sx={{
          display: 'flex',
          gap: 1.5,
          py: 1,
          px: 1,
          borderRadius: 1,
          bgcolor: blockActive ? 'grey.100' : 'transparent',
          '&:hover': {
            bgcolor: 'grey.100',
          },
          cursor: 'pointer',
          // Offset from list top when auto-scrolling (scrollIntoView block: 'start').
          scrollMarginTop: '50px',
        }}
      >
        <Chip
          color={blockActive ? 'info' : 'default'}
          variant={blockActive ? 'outlined' : 'filled'}
          size="small"
          label={block.timeLabel}
          sx={{ flexShrink: 0 }}
        />
        <Stack direction="column" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ color: block.speakerColor }}>
            {block.speakerLabel}
          </Typography>
          {block.isMusic ? (
            <Typography variant="body2" color="text.secondary" component="span">
              {formatMessage(musicSegmentMessage)}
            </Typography>
          ) : (
            <Box sx={{ typography: 'body2' }}>
              {block.words.map((word, index) => (
                <Box
                  component="span"
                  key={word.id}
                  sx={{ display: 'inline', color: blockActive ? '#000' : 'grey.700' }}
                >
                  {index > 0 ? ' ' : null}
                  <TranscriptionWord
                    text={word.text}
                    confidence={word.confidence}
                    showEfficiency={showWordEfficiency}
                    isHighlighted={isWordHighlighted(word, blockActive)}
                    isSearchMatch={matchWordIds.has(word.id)}
                    onClick={() => onWordClick(word.start)}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </Box>
    );
  },
);
